import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AccountLayout from '../../components/Account/AccountLayout';
import { supabase } from '../../supabase/client';
import { getPasswordError } from '../../utils/passwordValidation';
import { config } from '../../utils/config';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import BottomSheet from '../../components/BottomSheet/BottomSheet';
import DeleteAccountContent from '../../components/Account/DeleteAccountContent';
import './AccountSettingsPage.css';

const AccountSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, session, signOut, changePassword } = useAuth();
  const { isMobile } = useMobileDetection();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteStep, setDeleteStep] = useState('');
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<string | null>(null);

  // Handle change password
  const handleChangePassword = async () => {
    // Clear previous messages
    setChangePasswordError(null);
    setChangePasswordSuccess(null);
    
    // Validate passwords
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setChangePasswordError('All password fields are required');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }
    
    const passwordError = getPasswordError(newPassword);
    if (passwordError) {
      setChangePasswordError(passwordError);
      return;
    }
    
    if (newPassword === currentPassword) {
      setChangePasswordError('New password must be different from current password');
      return;
    }
    
    setChangePasswordLoading(true);
    
    try {
      const result = await changePassword(newPassword);
      
      if (result.success) {
        setChangePasswordSuccess('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowChangePassword(false);
      } else {
        setChangePasswordError(result.message || 'Failed to change password');
      }
    } catch (error: any) {
      setChangePasswordError(error.message || 'An error occurred');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Handle account deletion with progress tracking
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setDeleteError('Please type "DELETE MY ACCOUNT" exactly to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    setDeleteProgress(0);

    const steps = [
      { message: 'Verifying authentication...', progress: 10 },
      { message: 'Canceling subscriptions...', progress: 25 },
      { message: 'Removing account data...', progress: 50 },
      { message: 'Deleting saved files...', progress: 75 },
      { message: 'Finalizing deletion...', progress: 90 },
      { message: 'Account deleted successfully', progress: 100 }
    ];

    try {
      // Step 1: Verify authentication
      setDeleteStep(steps[0].message);
      setDeleteProgress(steps[0].progress);
      await new Promise(resolve => setTimeout(resolve, 800));

      const { data: { session: freshSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !freshSession) {
        throw new Error('Unable to verify authentication. Please sign in again.');
      }

      // Step 2: Start deletion process  
      setDeleteStep(steps[1].message);
      setDeleteProgress(steps[1].progress);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 3: Call backend deletion
      setDeleteStep(steps[2].message);
      setDeleteProgress(steps[2].progress);
      
      const response = await fetch(`${config.API_URL}/api/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshSession.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete account: ${response.status} ${errorText}`);
      }

      // Step 4: Storage cleanup
      setDeleteStep(steps[3].message);
      setDeleteProgress(steps[3].progress);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 5: Finalizing
      setDeleteStep(steps[4].message);
      setDeleteProgress(steps[4].progress);
      await new Promise(resolve => setTimeout(resolve, 600));

      // Step 6: Complete
      setDeleteStep(steps[5].message);
      setDeleteProgress(steps[5].progress);
      setDeleteSuccess(true);
      
      setTimeout(async () => {
        try {
          localStorage.clear();
          sessionStorage.clear();
          await supabase.auth.signOut({ scope: 'global' }).catch(() => {});
          await signOut().catch(() => {});
        } catch (cleanupError: any) {
          console.log('Cleanup error (non-critical):', cleanupError.message);
        }
        window.location.replace('/');
      }, 2000);
      
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setDeleteError(error.message || 'Failed to delete account. Please try again.');
      setDeleteProgress(0);
      setDeleteStep('');
    } finally {
      if (!deleteSuccess) {
        setIsDeleting(false);
      }
    }
  };

  if (!user) {
    return (
      <AccountLayout activeSection="account">
        <div className="account-settings-auth-required">
          <div className="account-settings-auth-content">
            <h1>Account Settings</h1>
            <p>Please sign in to access your account settings.</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to ZoningPal
            </button>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout activeSection="account">
      <div className="account-settings-page">
        <div className="account-settings-header">
          <h1>Account Settings</h1>
          <p className="account-settings-subtitle">
            Manage your security settings and account preferences.
          </p>
        </div>

        <div className="account-settings-content">
          {/* Change Password Success Message */}
          {changePasswordSuccess && (
            <div className="change-password-success">
              <div className="success-icon">✓</div>
              <div className="success-text">{changePasswordSuccess}</div>
            </div>
          )}
          
          {/* Security Settings Section */}
          <div className="account-settings-section">
            <h2>Security Settings</h2>
            
            <div className="change-password-section">
              <div className="setting-item">
                <div className="setting-description">
                  <h3>Change Password</h3>
                  <p>Update your account password for better security.</p>
                </div>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowChangePassword(!showChangePassword)}
                  disabled={changePasswordLoading}
                >
                  {showChangePassword ? 'Cancel' : 'Change Password'}
                </button>
              </div>
              
              {/* Change Password Form */}
              {showChangePassword && (
                <div className="change-password-form">
                  {/* Error Message */}
                  {changePasswordError && (
                    <div className="change-password-error">
                      <div className="error-icon">!</div>
                      <div className="error-text">{changePasswordError}</div>
                    </div>
                  )}
                  
                  <div className="password-fields">
                    <div className="password-field">
                      <label htmlFor="currentPassword">Current Password</label>
                      <input
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter your current password"
                        disabled={changePasswordLoading}
                      />
                    </div>
                    
                    <div className="password-field">
                      <label htmlFor="newPassword">New Password</label>
                      <input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter your new password"
                        disabled={changePasswordLoading}
                      />
                    </div>
                    
                    <div className="password-field">
                      <label htmlFor="confirmNewPassword">Confirm New Password</label>
                      <input
                        id="confirmNewPassword"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Confirm your new password"
                        disabled={changePasswordLoading}
                      />
                    </div>
                  </div>
                  
                  <div className="password-form-actions">
                    <button 
                      className="btn-primary"
                      onClick={handleChangePassword}
                      disabled={changePasswordLoading}
                    >
                      {changePasswordLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Danger Zone */}
          <div className="danger-zone">
            <h2>Danger Zone</h2>
            <div className="danger-content">
              <div className="danger-description">
                <h3>Delete Account</h3>
                <p>
                  Permanently delete your ZoningPal account and all associated data. 
                  This action cannot be undone.
                </p>
                <ul>
                  <li>All saved reports will be deleted</li>
                  <li>Your subscription will be cancelled</li>
                  <li>All account data will be permanently removed</li>
                </ul>
              </div>
              <button 
                className="btn-danger"
                onClick={() => setShowDeleteConfirmation(true)}
                disabled={isDeleting}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation - Mobile Bottom Sheet or Desktop Modal */}
        {showDeleteConfirmation && isMobile ? (
          <BottomSheet
            isOpen={showDeleteConfirmation}
            onClose={() => {
              setShowDeleteConfirmation(false);
              setDeleteConfirmText('');
              setDeleteError(null);
              setDeleteSuccess(false);
              setDeleteProgress(0);
              setDeleteStep('');
            }}
            title="Delete Account"
            className="delete-account"
          >
            <DeleteAccountContent
              deleteSuccess={deleteSuccess}
              isDeleting={isDeleting}
              deleteError={deleteError}
              deleteConfirmText={deleteConfirmText}
              deleteProgress={deleteProgress}
              deleteStep={deleteStep}
              onDeleteConfirmTextChange={setDeleteConfirmText}
              onDeleteAccount={handleDeleteAccount}
              onCancel={() => {
                setShowDeleteConfirmation(false);
                setDeleteConfirmText('');
                setDeleteError(null);
                setDeleteSuccess(false);
                setDeleteProgress(0);
                setDeleteStep('');
              }}
              isMobile={true}
            />
          </BottomSheet>
        ) : showDeleteConfirmation ? (
          <div className="delete-modal-overlay">
            <div className="delete-modal">
              <div className="delete-modal-header">
                <h2>Delete Account</h2>
                <button 
                  className="delete-modal-close"
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmText('');
                    setDeleteError(null);
                    setDeleteSuccess(false);
                    setDeleteProgress(0);
                    setDeleteStep('');
                  }}
                >
                  ×
                </button>
              </div>
              
              <div className="delete-modal-body">
                <DeleteAccountContent
                  deleteSuccess={deleteSuccess}
                  isDeleting={isDeleting}
                  deleteError={deleteError}
                  deleteConfirmText={deleteConfirmText}
                  deleteProgress={deleteProgress}
                  deleteStep={deleteStep}
                  onDeleteConfirmTextChange={setDeleteConfirmText}
                  onDeleteAccount={handleDeleteAccount}
                  onCancel={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmText('');
                    setDeleteError(null);
                    setDeleteSuccess(false);
                    setDeleteProgress(0);
                    setDeleteStep('');
                  }}
                  isMobile={false}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AccountLayout>
  );
};

export default AccountSettingsPage; 